import { Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { memo, useCallback, useMemo, useState } from "react";
import LoadingSpinner from '../../Components/LoadingSpinner';
import { useSelector } from "react-redux";
import { selectViewMenu } from "../../Selectors/ViewMenuSelectors";
import SubHeader from "../../Layouts/SubHeader";
import Header from "../../Layouts/Header";
import { filterEntries, sortEntries, useMyListEntries } from "../../Utils/Entry";
import { generatePathCategoryEntry, generatePathEntry, getQueryParamsAndParse, mergeParamsIntoQueryString } from "../../Utils/Routes";
import Page from "../../Components/Page";
import LargeGridViewEntryList from "../../Components/Entry/List/GridViewEntryList";
import ListViewEntryList from "../../Components/Entry/List/ListViewEntryList";
import SummaryViewEntryList from "../../Components/Entry/List/SummaryViewEntryList";
import { useAwards } from "../../Utils/Award";
import { useLocation, Link, useNavigate } from "react-router-dom";
import EntryListRoot from "../../Components/Entry/List/EntryListRoot";
import { useScrollToPositionId } from "../../Utils/ScrollToPosition";
import { useScrollToTop } from "../../Utils/ScrollToTop";

import "./MyListPage.scss";
import { expandCategoryPageEntries, filterMyListEntryData } from "../../Components/Entry/List/GenericEntryList";

const useStyles = makeStyles((theme) => ({
    numEntries: {
        textAlign: "right",
        fontSize: `clamp(10px, 2vw, 14px)`,
        lineHeight: 1,
        margin: `0 ${theme.spacing(4)}`,
        padding: `0`,
        color: theme.palette.secondary.light,
    },
}));

export default memo(function MyListPage () {
    const classes = useStyles();

    const navigate = useNavigate();
    const location = useLocation();
    const { search: queryString } = location;

    const { viewType } = useSelector(selectViewMenu);
    const { sortType } = useSelector(selectViewMenu);

    const filterParams = useMemo(() => {
        return getQueryParamsAndParse(queryString, {
            searchQuery: { type: "string" },
        }, {
            removeUnknown: true,
        });
    }, [queryString]);
    
    const [searchQueryTextSearched, setSearchQueryTextSearched] = useState(filterParams.searchQuery);

    console.log("searchQueryTextSearched", searchQueryTextSearched);

    const handleSearchSubmit = useCallback((text) => {
        if(text === (searchQueryTextSearched ?? "")) return;

        navigate({ 
            search: mergeParamsIntoQueryString(location.search, {
                searchQuery: text ? text : undefined,
            }),
        });
        setSearchQueryTextSearched(text);
    }, [searchQueryTextSearched, navigate, location.search]);
    
    const awards = useAwards();
    const allEntries = useMyListEntries(awards);
    console.log("allEntries", allEntries);

    const entries = useMemo(() => {
        if(!allEntries || !awards) return null;

        const filteredEntries = filterEntries(allEntries, { searchQuery: searchQueryTextSearched });
        const sortedEntries = sortEntries(filteredEntries, sortType);
        return sortedEntries;
    }, [allEntries, awards, searchQueryTextSearched, sortType]);
    console.log("entries", entries);

    const totalEntries = allEntries?.length;
    const totalVisible = entries?.length;

    // useEffect(() => {
    //     /* If there are parameters they get priority over the state */
    //     const filterParams = getQueryParamsAndParse(queryString, {});
    //     setSearchQueryTextCurrent(filterParams.searchQuery ?? "");
    // }, [queryString]);

    const title = "My List";

    const isLoading = !entries || !awards;

    const entryListLinkComponent = memo(useCallback(function EntryListLinkComponent({ entry, entryCategoryId, ...props }) {
        
        const baseEntryLink = entryCategoryId
            ? generatePathCategoryEntry(entry.awardId, entryCategoryId, entry.id)
            : generatePathEntry(entry.awardId, entry.id)

        const entryLink = baseEntryLink + '?from=mylist'; //+ (backUrlQueryString ? "?" + backUrlQueryString : "");

        return <Link to={entryLink} {...props} />;
    }, []));

    const displayViewType = viewType;

    const ListComponent = {
        summaryView: SummaryViewEntryList,
        largeView: LargeGridViewEntryList,
        listView: ListViewEntryList,
    }[displayViewType] ?? SummaryViewEntryList;

    useScrollToTop();
    useScrollToPositionId(!isLoading);

    const categoriesEntriesData = expandCategoryPageEntries(entries);
    const entriesData = filterMyListEntryData(categoriesEntriesData);

    return (
        <Page title={title}>
            { isLoading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Header />
                    <SubHeader 
                        showMyListInMenu={true}
                        currentAward={null}
                        currentFixedCategories={null}
                        entries={allEntries} 
                        activeFilters={null}
                        backUrl={""}
                        searchQueryText={searchQueryTextSearched}
                        onSearchSubmit={handleSearchSubmit}
                    />

                    <div className="ListTemplate" style={{marginTop: "1rem"}}>
                        <EntryListRoot justifyContent="center">
                            {allEntries?.length ? (
                                <>
                                    <Typography variant="button" className={["upper", classes.numEntries].join(" ")} component="div">
                                        {`${totalVisible} ${totalEntries > totalVisible ? `of ${totalEntries}`:''} entries`}
                                    </Typography>

                                    <ListComponent
                                        entriesData={entriesData}
                                        linkComponent={entryListLinkComponent}
                                        // categoryLinkComponent={entryListCategoryLinkComponent}
                                    />
                                </>
                            ):(
                                <div className="ListTemplate__emptymessage typography-h6">
                                    <p>
                                        The My List page is a place where you can curate your judging across awards.<br />
                                        To add entries to this page go to one of the open awards, 
                                        locate an entry and click on the Add to My List button.
                                    </p>

                                    <p>
                                        Please note that entries will automatically be removed from this page once 
                                        the viewing period for the award closes.
                                    </p>
                                </div>
                            )}
                        </EntryListRoot>
                    </div>
                </>
            )}
        </Page>
    );
});
